'use client'

import React from 'react'
import Image from 'next/image'
import { Container, Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import { Button } from '@quark/button'
import { Reveal, MultiReveal, Animations } from '@quark/animation'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'

import isMobile from 'ismobilejs'
import styles from './getQuote.module.scss'

interface QuoteItem {
  iconUrl?: {
    url: string
  }
  title?: string
}

interface BenefitsCardProps {
  title?: string
  subtitle?: string
  actionText?: string
  quoteList?: QuoteItem[]
  heroImage?: {
    url: string
  }
  redirectionLink?: string
}

const BenefitsCard = (data: BenefitsCardProps) => {
  const isMobileDevice = isMobile()?.phone

  return (
    <div className={styles.getQuoteContainer}>
      <Container
        type="root"
        paddingTop={{ lg: 'spacing-72', md: 'spacing-56', sm: 'spacing-40' }}
        paddingBottom={{ lg: 'spacing-72', md: 'spacing-56', sm: 'spacing-40' }}
        zIndex="content"
        position="relative"
        paddingLeft="spacing-16"
        paddingRight="spacing-16">
        <Flex
          justifyContent="space-between"
          gap="spacing-32"
          flexDirection={{ lg: 'row', md: 'row', sm: 'column-reverse' }}>
          <Flex
            flexDirection="column"
            gap={{
              lg: 'spacing-32',
              md: 'spacing-24',
              sm: 'spacing-24',
            }}>
            <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
              <Container paddingBottom="spacing-16">
                <Text
                  as="h2"
                  fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-3xl/bold' }}
                  color="base/primary">
                  <span dangerouslySetInnerHTML={{ __html: data?.title || '' }} />
                </Text>
              </Container>
              <Text
                as="h2"
                fontStyle={{ lg: 'um_text-xl/normal', md: 'um_text-lg/normal', sm: 'um_text-base/normal' }}
                color="base/secondary">
                {data?.subtitle}
              </Text>
            </Reveal>
            <PropertyControlledComponent controllerProperty={!isMobileDevice} fallback={null}>
              <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 400 }}>
                <Button
                  type="solid"
                  size="lg"
                  onClick={() => data?.redirectionLink && window.open(data?.redirectionLink, '_self')}>
                  <Text fontStyle="um_text-lg/medium" color="base/inverted primary">
                    {data?.actionText}
                  </Text>
                </Button>
              </Reveal>
            </PropertyControlledComponent>
            <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
              <Flex
                flexDirection="column"
                gap={{
                  lg: 'spacing-20',
                  md: 'spacing-16',
                  sm: 'spacing-16',
                }}
                paddingTop={{ lg: 'spacing-32', md: 'spacing-24', sm: 'spacing-0' }}>
                <MultiReveal
                  as={Flex}
                  flexDirection="column"
                  gap={{
                    lg: 'spacing-20',
                    md: 'spacing-16',
                    sm: 'spacing-16',
                  }}
                  animationDelays={(i: number) => 300 + i * 100}
                  animationProps={{ animation: Animations.FadeIn50px }}>
                  {data?.quoteList?.map((benefit, index) => (
                    <div className={styles.benefitListItem} key={index}>
                      {benefit?.iconUrl?.url && (
                        <Image
                          src={benefit.iconUrl.url}
                          alt="check-mark"
                          width={isMobileDevice ? 16 : 24}
                          height={isMobileDevice ? 16 : 24}
                        />
                      )}
                      <Text
                        as="div"
                        fontStyle={{
                          lg: 'um_text-lg/normal',
                          md: 'um_text-base/normal',
                          sm: 'um_text-sm/normal',
                        }}
                        color="base/primary">
                        {benefit?.title}
                      </Text>
                    </div>
                  ))}
                </MultiReveal>
              </Flex>
            </Reveal>
            <PropertyControlledComponent controllerProperty={isMobileDevice} fallback={null}>
              <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 400 }}>
                <Button
                  type="solid"
                  size="lg"
                  onClick={() => data?.redirectionLink && window.open(data?.redirectionLink, '_self')}>
                  <Text fontStyle="um_text-lg/medium" color="base/inverted primary">
                    {data?.actionText}
                  </Text>
                </Button>
              </Reveal>
            </PropertyControlledComponent>
          </Flex>
          <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 400 }}>
            <PropertyControlledComponent controllerProperty={data?.heroImage?.url} fallback={null}>
              <img src={data?.heroImage?.url} alt="Claim resolved on phone" className={styles.heroImage} />
            </PropertyControlledComponent>
          </Reveal>
        </Flex>
      </Container>
      <div className={styles.circle1} />
      <div className={styles.circle2} />
    </div>
  )
}

export default BenefitsCard
